'use client';

import {
  Pagination,
  PaginationLink,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationContent,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface ListPaginationProps {
  totalPages: number;
  currentPage: number;
  pageSize: number;
  handlePageChange: (_page: number) => void;
  totalCount: number;
}

export default function ListPagination({
  totalPages,
  pageSize,
  currentPage,
  handlePageChange,
  totalCount,
}: ListPaginationProps) {
  const pages = [];
  const maxVisible = 3;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);

  // Adjust start if we're near the end
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <div className="w-full">
      <Pagination>
        <PaginationContent
          className="
            flex flex-col items-center gap-2 w-full
            sm:flex-row sm:justify-between sm:items-center
          "
        >
          <div className="text-sm text-muted-foreground text-center sm:text-left mb-1 sm:mb-0">
            {(currentPage - 1) * pageSize + 1} -{' '}
            {Math.min((currentPage - 1) * pageSize + pageSize, totalCount)} of {totalCount}{' '}
            properties
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {currentPage > 1 && (
              <PaginationItem className="cursor-pointer">
                <PaginationPrevious
                  onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                />
              </PaginationItem>
            )}

            {pages.length > 1 &&
              pages.map((page: number) => (
                <PaginationItem key={page} className="cursor-pointer">
                  <PaginationLink
                    onClick={() => handlePageChange(page)}
                    isActive={currentPage === page}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
            {end < totalPages && <PaginationEllipsis />}

            {totalPages > currentPage && (
              <PaginationItem className="cursor-pointer">
                <PaginationNext
                  onClick={() => totalPages > currentPage && handlePageChange(currentPage + 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}
          </div>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
